import {ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {filter, switchMap, take} from 'rxjs/operators';
import {
  CrossReferenceSpecializationType,
  DocumentForm,
  DocumentState,
  EntityClassType,
  EntityType,
  FileState,
  InternalNotificationKey,
  ObjectClass,
  SettlementType
} from '|api/commons';
import {
  ApiCalendarHelperService,
  DocumentAuthorizationResult,
  DocumentAuthorizedOperation,
  FileAuthorizedOperation
} from '|api/core';
import {
  ApiDocumentService,
  ApiOwnDocumentService,
  ApiReceivedDocumentService,
  ApiSettlementService,
  CustomFieldValueDto,
  DocumentAllowableObjectClass,
  DocumentDto,
  FileDto,
  OwnDocumentUpdateDto,
  ReceivedDocumentDto,
  ReceivedDocumentUpdateDto,
} from '|api/document';
import {
  BasicRegistersService,
  CodebookService,
  CrossReferenceSearchService,
  CustomFieldValue,
  DisposalScheduleRetentionTriggerWidgetComponent,
  DisposalTipDetailLevel,
  DocumentDetailService,
  DocumentDtoWithAuthorization,
  DocumentFileCustomFieldsSectionComponent,
  DocumentRegistrationTimelineLinkComponent,
  DocumentTypeSelectorComponent,
  EmpowermentComponent,
  EntityClassSelectorComponent,
  EntityClassSelectorUsage,
  EntityItemComponent,
  EntityWithRefNumber,
  EsslComponentDto,
  EsslComponentLinkComponent,
  EsslComponentSearchService,
  FileDtoWithAuthorization,
  FindFunctionalPositionByIdPipe,
  formatDisposalOperation,
  isAuthorizedOperationGranted,
  KeywordsSelectorComponent,
  load,
  ObjectDetailPart,
  ObjectDetailSubmitActionType,
  OrganizationalStructureService,
  PortraitComponent,
  prepareCustomFieldValuesForBackend,
  REF_NUMBER_UNAVAILABLE,
  RefNumberLinkComponent,
  RetentionTriggerWidgetMode,
  SecurityCategorySelectorComponent,
  WITHOUT_REF_NUMBER
} from '|shared';
import {DocumentDetailComponent} from '../../document-detail.component';
import {
  createDocumentDetailFormGroup,
  setDocumentFormDisabledState,
} from '../../../document-shared/validation/document-form-definition';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  DocumentFileDetailPreviewComponent
} from '../../../document-shared/components/abstract-object-detail-preview.component';
import {
  AutoFocusDirective,
  ButtonComponent,
  CopyToClipboardButtonComponent,
  InaccessibleDirective,
  LabelComponent,
  LoadingIndicatorService,
  LocalizedDatePipe,
  LocalizedDatetimePipe,
  PassedDeadlineWarningDirective,
  SectionComponent,
  SpinnerComponent
} from '@icz/angular-essentials';
import {
  DatePickerComponent,
  formatAsLocalIsoDate,
  FormAutocompleteComponent,
  FormFieldComponent,
  IczFormArray,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption
} from '@icz/angular-form-elements';
import {ActivatedRoute} from '@angular/router';
import {FilterOperator, getDefaultSearchParams, SearchParams, SortDirection} from '@icz/angular-table';
import {ReactiveFormsModule} from '@angular/forms';
import {
  ObjectAssociationAlertComponent
} from '../../../document-shared/components/object-association-alert/object-association-alert.component';
import {
  ObjectDetailPreviewTransferInfoSectionComponent
} from '../../../document-shared/components/object-detail-preview-transfer-info-section/object-detail-preview-transfer-info-section.component';
import {
  EsslComponentCountsComponent
} from '../../../document-shared/components/essl-component-counts/essl-component-counts.component';
import {AsyncPipe} from '@angular/common';
import {
  CaseFileAssociationAlertComponent
} from '../../../document-shared/components/case-file-association-alert/case-file-association-alert.component';
import {
  StorageUnitAssociationAlertComponent
} from '../../../document-shared/components/storage-unit-association-alert/storage-unit-association-alert.component';
import {CheckUnsavedFormPageService, enumToOptions, namedDtosToOptions} from '@icz/angular-application-core';
import {DocumentTypeDto, StatutoryPeriodUnit} from '|api/codebook';
import {add} from 'date-fns';
import {
  ActionRoute,
  ApplicationConfigService,
  ApplicationRoute,
  createAbsoluteRoute,
  DocumentDetailRoute,
  DocumentsRoute,
  DocumentToastService,
  DocumentToastType, EsslHistoryService
} from '|ui-essl-core';

enum DocumentDetailPreviewLoaderId {
  DOCUMENT_COMPONENTS = 'DOCUMENT_COMPONENTS',
}

@Component({
  selector: 'icz-document-detail-preview',
  templateUrl: './document-detail-preview.component.html',
  styleUrls: ['./document-detail-preview.component.scss'],
  providers: [CheckUnsavedFormPageService, LocalizedDatePipe],
  imports: [AutoFocusDirective, IczFormContainerDirective, ReactiveFormsModule, InaccessibleDirective, ObjectAssociationAlertComponent, RefNumberLinkComponent, SectionComponent, CopyToClipboardButtonComponent, DocumentRegistrationTimelineLinkComponent, FormFieldComponent, FormAutocompleteComponent, DatePickerComponent, PassedDeadlineWarningDirective, LabelComponent, EntityItemComponent, SpinnerComponent, EsslComponentLinkComponent, ButtonComponent, PortraitComponent, DocumentTypeSelectorComponent, EntityClassSelectorComponent, DisposalScheduleRetentionTriggerWidgetComponent, SecurityCategorySelectorComponent, KeywordsSelectorComponent, DocumentFileCustomFieldsSectionComponent, EmpowermentComponent, ObjectDetailPreviewTransferInfoSectionComponent, EsslComponentCountsComponent, AsyncPipe, LocalizedDatetimePipe, TranslatePipe, CaseFileAssociationAlertComponent, StorageUnitAssociationAlertComponent, FindFunctionalPositionByIdPipe]
})
export class DocumentDetailPreviewComponent extends DocumentFileDetailPreviewComponent<DocumentDtoWithAuthorization> implements OnInit {
  protected loadingService = inject(LoadingIndicatorService);
  protected documentDetailService = inject(DocumentDetailService);
  protected applicationConfigService = inject(ApplicationConfigService);
  private apiDocumentService = inject(ApiDocumentService);
  private apiOwnDocumentService = inject(ApiOwnDocumentService);
  private apiReceivedDocumentService = inject(ApiReceivedDocumentService);
  private codebookService = inject(CodebookService);
  private historyService = inject(EsslHistoryService);
  private cd = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private organizationalStructureService = inject(OrganizationalStructureService);
  private basicRegistersService = inject(BasicRegistersService);
  private documentToastService = inject(DocumentToastService);
  private translateService = inject(TranslateService);
  private apiSettlementService = inject(ApiSettlementService);
  private searchService = inject(EsslComponentSearchService);
  private parentDocumentDetailComponent = inject(DocumentDetailComponent, {optional: true});
  private crossReferenceSearchService = inject(CrossReferenceSearchService);
  private apiCalendarHelperService = inject(ApiCalendarHelperService);
  private datePipe = inject(LocalizedDatePipe);

  readonly ApplicationRoute = ApplicationRoute;
  readonly DocumentsRoute = DocumentsRoute;
  readonly ActionRoute = ActionRoute;
  readonly LOSS_RECORD_LOADING = 'loss_record_loading';

  private _objectClass!: DocumentAllowableObjectClass;

  form!: IczFormGroup;

  documentStateOptions: IczOption[] = enumToOptions('documentState', DocumentState);

  documentFormOptions: IczOption[] = enumToOptions('documentForm', DocumentForm);

  basicRegisterOptions: IczOption[] = [];

  functionalPositionOptions$: Observable<IczOption[]> = this.organizationalStructureService.functionalPositionsOptions();

  issdApplicationOptions$: Observable<IczOption[]> = this.codebookService.issdApplications().pipe(
    namedDtosToOptions
  );

  file: Nullable<FileDtoWithAuthorization>;
  isFileUnavailable = false;
  retentionTriggerValidationMode = new BehaviorSubject<RetentionTriggerWidgetMode>(RetentionTriggerWidgetMode.ENABLED);

  documentSettlementTypeOptions = enumToOptions('settlementType', SettlementType);

  settledByDocument: Nullable<DocumentDto>;
  lostRecordDocument: Nullable<DocumentDto>;

  documentComponents: EsslComponentDto[] = [];

  componentsCount = 0;

  customFieldsValidityReferenceDate: Nullable<Date>;

  readonly ObjectClass = ObjectClass;

  document$ = this.documentDetailService.object$;
  customFieldIdToWatch: Nullable<number>;
  externalRetentionTriggerOffset: Nullable<number>;
  documentTypeIdSubscription: Nullable<Subscription>;
  documentTypes: DocumentTypeDto[] = [];
  deadlineWarning = new Date(0).toISOString();
  deliveryDate: Nullable<string>;

  get entityType() {
    return (this.documentDetailService.object as DocumentDtoWithAuthorization).entityType!;
  }

  get auditInfo() {
    return (this.documentDetailService.object as DocumentDtoWithAuthorization).auditInfo!;
  }

  get entityClassId() {
    return this.form.get('entityClassId')!.value;
  }

  get documentTypeId() {
    return this.form.get('documentTypeId')!.value;
  }

  get fileId() {
    return this.form.get('fileId')!.value;
  }

  get refNumberValue() {
    if (this.canShowDocumentProfile || this.applicationConfigService.objectExistenceCheckObjectRefNumberVisible()) {
      return this.form.get('refNumber')!.value ?? this.translateService.instant(WITHOUT_REF_NUMBER);
    }
    else {
      return this.translateService.instant(REF_NUMBER_UNAVAILABLE);
    }
  }

  get isDocumentSettled() {
    return this.form.get('documentState')?.value === DocumentState.SETTLED;
  }

  get isDocumentDeactivated() {
    return this.form.get('documentState')?.value === DocumentState.DEACTIVATED;
  }

  get isDocumentLost() {
    return this.form.get('documentState')?.value === DocumentState.LOST;
  }

  get isDocumentExported() {
    return this.form.get('documentState')?.value === DocumentState.EXPORTED;
  }

  get isDocumentRegisteredExternally() {
    return this.form.get('documentState')?.value === DocumentState.REGISTERED_EXTERNALLY;
  }

  get externalRegistryMoveDate() {
    return this.form.get('externalRegisterMoveDate')!.value;
  }

  get isReceivedDocument() {
    return this._objectClass === DocumentAllowableObjectClass.RECEIVED_DOCUMENT || this._objectClass === DocumentAllowableObjectClass.RECEIVED_DOCUMENT_DIGITAL;
  }

  get isInExternalApplication() {
    return !isNil(this.form.get('issdId')!.value);
  }

  get settlementType() {
    return this.form.get('settlementType')?.value as SettlementType;
  }

  get canShowDocumentProfile() {
    return isAuthorizedOperationGranted(this.authorization, DocumentAuthorizedOperation.DOCUMENT_SHOW_PROFILE);
  }

  get canShowDocumentComponents() {
    return isAuthorizedOperationGranted(this.authorization, DocumentAuthorizedOperation.DOCUMENT_SHOW_COMPONENTS);
  }

  get canModifyDocumentProfile() {
    return isAuthorizedOperationGranted(this.authorization, DocumentAuthorizedOperation.DOCUMENT_MODIFY_PROFILE);
  }

  get objectExistenceComponentsVisible() {
    return this.applicationConfigService.objectExistenceCheckDocumentComponentsVisible();
  }

  readonly PREVIEW_COMPONENTS_TO_SHOW = 5;
  readonly DocumentDetailPreviewLoaderId = DocumentDetailPreviewLoaderId;

  override ngOnInit(): void {
    super.ngOnInit();

    combineLatest([
      this.documentDetailService.object$,
      this.basicRegistersService.getBasicRegisters().pipe(
        namedDtosToOptions,
      ),
      this.codebookService.documentTypes()
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([document, basicRegisterOptions, documentTypes]) => {
      if (document) {
        this.basicRegisterOptions = basicRegisterOptions;
        this.authorization = document.authorization as DocumentAuthorizationResult;
        this._objectClass = document.objectClass;
        if (this.isReceivedDocument) {
          this.deliveryDate = (document as ReceivedDocumentDto).deliveryDate;
        }

        this.getFileForDocument();
        this.getStorageUnit(document);
        this.form = createDocumentDetailFormGroup();

        this.initializePreviewForm(document);
        this.loadDocumentComponentsPreview();
      }
      this.documentTypes = documentTypes;
    });
  }

  private initializePreviewForm(document: DocumentDtoWithAuthorization) {
    this.documentDetailService.showSubmitBar = true;

    const canEditPreviewForm = this.canShowDocumentProfile && this.canModifyDocumentProfile;
    this.documentDetailService.form$.next(this.form);

    if (this.canShowDocumentProfile) {
      const customFieldsForm = this.form.get('customFieldValues') as IczFormArray;
      customFieldsForm.enable();
      customFieldsForm.setSize((document as DocumentDtoWithAuthorization).customFieldValues?.length ?? 0);
    }
    this.form.get('resolutionDate')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      if (value) {
        if (!this.isDocumentSettled) {
          setTimeout(() => {
            this.deadlineWarning = value;
          });
        }
      }
    }
  );

    this.form.patchValue(document);
    this.form.patchValue(document.currentQuantification as any);
    this.initCustomFieldsValidityReferenceDate();

    /* This whole section of code is dealing with enable/disable state of form. It must be enclosed in setTimeout
    *  otherwise runtime switching from disabled -> enabled and vice versa won't work properly.
    *  problem is that when document is reloaded the reference to this.form is changed and messes up rerendering of form state.
    *  */
    setTimeout(() => {
      this.form.enable();
      setDocumentFormDisabledState(this.form);

      if ((document as DocumentDto).settlementId) {
        this.apiSettlementService.settlementFindById({
          id: (document as DocumentDto).settlementId!
        }).subscribe(settlement => {
          this.form.get('settlementFunctionalPositionId')?.setValue(settlement.auditInfo?.createdByFunctionalPositionId);
          this.form.get('settlementDate')?.setValue(settlement.settlementDate);
          this.form.get('settlementType')?.setValue(settlement.type);
          this.form.get('settlementContent')?.setValue(settlement.content);
          this.form.get('settlementReason')?.setValue(settlement.reason);
          this.form.get('customFieldValues')?.disable();
          this.initCustomFieldsValidityReferenceDate();
          if (settlement.type === SettlementType.BY_DOCUMENT) {
            this.apiDocumentService.documentFindById({id: settlement.relatedDocumentId!}).subscribe( doc => {
              this.settledByDocument = doc;
            });
          }
          if (settlement.type === SettlementType.BY_OTHER_METHOD) {
            this.fillOtherSettlementMethod(this.codebookService, this.translateService, settlement.settlementMethodId);
          }
        });
        this.form.get('empowerment')?.disable();
        this.form.get('documentForm')?.disable();
        this.form.get('label')?.disable();
        this.form.get('resolutionDate')?.disable();
        this.form.get('senderRefNr')?.disable();
        this.form.get('settlementDate')?.disable();
        this.form.get('settlementReason')?.disable();
        this.form.get('settlementContent')?.disable();
        this.form.get('disposalOperationCode')?.setValue(formatDisposalOperation(document.disposalOperationCode, this.translateService));
      }

      if (this.isDocumentLost) {
        const searchParams = getDefaultSearchParams();
        searchParams.filter.push({
          fieldName: 'specialization',
          operator: FilterOperator.equals,
          value: CrossReferenceSpecializationType.DOCUMENT_LOSS,
        });
        this.loadingService.startLoading(this, this.LOSS_RECORD_LOADING);
        this.crossReferenceSearchService.findCrossReferenceEntitiesForRelatedView(
          this.documentDetailService.objectId, EntityType.DOCUMENT, searchParams
        ).subscribe(result => {
          // if document is lost then there should be exactly 1 cross reference of type DOCUMENT_LOSS
          if (result.content.length === 1) {
            this.apiDocumentService.documentFindById({id: result.content[0].entityReferringToId}).subscribe( doc => {
              this.lostRecordDocument = doc;
              this.loadingService.endLoading(this, this.LOSS_RECORD_LOADING);
            });
          }
        });
      }

      if ((document as DocumentDtoWithAuthorization).fileId) {
        this.form.get('entityClassId')?.disable();
      }

      if (!canEditPreviewForm || this.isDocumentDeactivated || this.isDocumentExported || this.isDocumentLost || this.isDocumentRegisteredExternally || this.documentDetailService.lockedState || this.isInExternalApplication) {
        this.form.disable();
      } else {
        if (this.isDocumentSettled) {
          if (this.file && this.file.fileState === FileState.CLOSED) {
            this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.DISABLED);
          } else {
            this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.VALIDATED);
          }
        } else {
          this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.ENABLED_START_YEAR);
        }

        if ((document as DocumentDtoWithAuthorization).documentTypeId) {
          const selectedType = this.documentTypes.find(type => type.id === (document as DocumentDtoWithAuthorization).documentTypeId)!;
          this.onExternalRetentionIdSelected(selectedType.externalRetentionTriggerId);
        }
      }

      this.initDocumentTypeChangeListener();
      this.cd.detectChanges();
    });

  }

  private getFileForDocument() {
    this.documentDetailService.parentFileForDocument$.subscribe({
      next: file => {
        this.isFileUnavailable = false;

        if (file) {
          this.file = file;
          this.getCaseFile(file);
        }
        else {
          this.file = null;
          this.caseFileInfo = null;
        }
      },
      error: _ => {
        this.isFileUnavailable = true;
      }
    });
  }

  private handleResolutionDateChange(dateToChange: Date, resolutionDateControl: IczFormControl) {
    const currentDate = formatAsLocalIsoDate(resolutionDateControl.value);
    const newDate = formatAsLocalIsoDate(dateToChange);
    if (resolutionDateControl.value) {
      if (currentDate !== newDate) {
        this.dialogService.openQuestionDialogWithAnswer({
          title: 'Změna hodnoty pro Termín vyřízení',
          question: `Dokument má zadaný termín vyřízení. Opravdu chcete přebrat termín vyřízení z typu dokumentu?`,
          description: `Aktuálně nastavený termín vyřízení na dokumentu je {{oldDate}}. Nový datum pro termín vyřízení bude {{newDate}}`,
          descriptionContext: {
            oldDate: this.datePipe.transform(currentDate),
            newDate: this.datePipe.transform(newDate)
          },
          leftButtonTitle: 'Přebrat datum'
        }).subscribe(doChange => {
          if (doChange) {
            resolutionDateControl.setValue(dateToChange.toISOString());
          }
        });
      }
    } else {
      resolutionDateControl.setValue(dateToChange.toISOString());
    }
  }

  onSubmit(submitActionType: ObjectDetailSubmitActionType) {
    const formValue = this.form.getRawValue();

    if (this.isDocumentSettled && ((this.form.get('triggerEventCheckYear')!.dirty || this.form.get('yearOfRetentionPeriodStart')!.dirty) || this.customFieldIdToWatch)) {
      let fieldValues: CustomFieldValueDto[] = [];
      if (this.customFieldIdToWatch) {
        fieldValues = prepareCustomFieldValuesForBackend(formValue.customFieldValues).filter(val => val.customFieldId === this.customFieldIdToWatch);
      }
      this.loadingService.doLoading(
        this.apiSettlementService.settlementUpdateTriggerYears({body: {
            operationEntityDtos: [{entityType: EntityType.DOCUMENT, id: this.documentDetailService.object.id}],
            triggerEventCheckYear: this.form.get('triggerEventCheckYear')!.value,
            yearOfRetentionPeriodStart: this.form.get('yearOfRetentionPeriodStart')!.value,
            customFieldValues: fieldValues
        }}),
        this.parentDocumentDetailComponent!
      ).subscribe(_ => {
        this.finishUpdate(({id: this.documentDetailService.object.id, subject: this.documentDetailService.object.subject}) as DocumentDto, submitActionType);
      });
    } else {
      if (this.canModifyDocumentProfile) {
        const updateDto: OwnDocumentUpdateDto | ReceivedDocumentUpdateDto = {
          assignRefNr: formValue.assignRefNr,
          componentPhysicalLocation: formValue.componentPhysicalLocation,
          description: formValue.description,
          documentForm: formValue.documentForm,
          documentState: formValue.documentState,
          documentTypeId: formValue.documentTypeId,
          entityClassId: formValue.entityClassId,
          keywordIds: formValue.keywordIds,
          executionDate: formValue.executionDate,
          label: formValue.label,
          note: formValue.note,
          objectClass: formValue.objectClass,
          resolutionDate: formValue.resolutionDate,
          securityCategoryId: formValue.securityCategoryId,
          senderRefNr: formValue.senderRefNr,
          subject: formValue.subject,
          empowerment: formValue.empowerment,
          relatedEvidence: formValue.relatedEvidence,
          documentSuspensions: formValue.documentSuspensions,
          yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
          triggerEventCheckYear: formValue.triggerEventCheckYear,
          customFieldValues: prepareCustomFieldValuesForBackend(formValue.customFieldValues),
        };

        this.documentDetailService.object$.pipe(
          filter(Boolean),
          take(1)
        ).subscribe(document => {
          this.checkCrossReferences([document.id], [])
            .subscribe((crossUpdate: boolean) => {
            let update$: Nullable<Observable<DocumentDto>>;

            if ((document as any).entityType === EntityType.RECEIVED_DOCUMENT) {
              update$ = this.apiReceivedDocumentService.receivedDocumentUpdate(
                {
                  id: document.id,
                  body: {...updateDto, updateCrossReferencedObjects: crossUpdate},
                }
              );
            } else {
              update$ = this.apiOwnDocumentService.ownDocumentUpdate(
                {
                  id: document.id,
                  body: {...updateDto, updateCrossReferencedObjects: crossUpdate},
                }
              );
            }
            if (update$) {
              this.loadingService.doLoading(
                update$,
                this.parentDocumentDetailComponent!
              ).subscribe(result => {
                this.finishUpdate(result, submitActionType);
              });
            }
          });
        });
      }
    }
  }

  getFileEntity(file: FileDto): EntityWithRefNumber {
    return {...file, permissions: this.authorization} as EntityWithRefNumber;
  }

  onComponentMetadataChange() {
    this.loadDocumentComponentsPreview();
  }

  navigateToComponents() {
    this.document$.pipe(
      filter(Boolean),
      take(1),
    ).subscribe(document => {
      this.router.navigateByUrl(createAbsoluteRoute(
        ApplicationRoute.DOCUMENTS,
        DocumentsRoute.DOCUMENT,
        document.id,
        DocumentDetailRoute.COMPONENTS,
        this.route.snapshot.queryParams,
      ));
    });
  }

  private finishUpdate(result: DocumentDto, submitActionType: ObjectDetailSubmitActionType) {
    this.documentToastService.dispatchDocumentInfoToast(DocumentToastType.DOCUMENT_UPDATED, {
      [InternalNotificationKey.DOCUMENT_ID]: result.id,
      [InternalNotificationKey.DOCUMENT_SUBJECT]: result.subject,
    });
    this.form.markAsPristine();
    this.documentDetailService.submitFinished$.next();

    if (submitActionType === ObjectDetailSubmitActionType.SAVE_AND_CLOSE) {
      this.historyService.routeBack(createAbsoluteRoute(ApplicationRoute.DOCUMENTS));
    }
    else {
      this.documentDetailService.alreadySaved = true;
      this.documentDetailService.reloadObject({
        [ObjectDetailPart.OBJECT_DATA]: load(),
      });

      if (!isNil(this.hideSubmitBarTimeout)) {
        clearTimeout(this.hideSubmitBarTimeout);
      }

      this.hideSubmitBarTimeout = setTimeout(() => {
        this.documentDetailService.alreadySaved = false;
      }, 5000) as unknown as number;
    }
  }

  onCustomFieldValueChange(value: CustomFieldValue) {
    let parsedDate: Nullable<Date> = null;

    if (value && !Array.isArray(value)) {
      parsedDate = new Date(value);
    }

    const yearOfRetentionPeriodControl = this.form.get('yearOfRetentionPeriodStart')!;

    if (!parsedDate) {
      yearOfRetentionPeriodControl.setValue(null);
    } else {
      if (this.externalRetentionTriggerOffset) {
        yearOfRetentionPeriodControl.setValue(parsedDate.getFullYear() + this.externalRetentionTriggerOffset);
      } else {
        yearOfRetentionPeriodControl.setValue(parsedDate.getFullYear());
      }
    }
  }

  onExternalRetentionIdSelected(disposalScheduleId: Nullable<number>) {
    if (disposalScheduleId) {
      this.codebookService.externalRetentionTriggers().subscribe(triggers => {
        const selected = triggers.find(trigger => trigger.id === disposalScheduleId)!;
        if (selected.customFieldId) {
          this.customFieldIdToWatch = selected.customFieldId;
          this.externalRetentionTriggerOffset = selected.triggerEventYearOffset;
          this.retentionTriggerValidationMode.next(RetentionTriggerWidgetMode.DISABLED);
        }
      });
    }

  }

  private loadDocumentComponentsPreview() {
    if (this.canShowDocumentComponents || this.objectExistenceComponentsVisible) {
      const searchParams: SearchParams = {
        page: 0,
        size: this.PREVIEW_COMPONENTS_TO_SHOW,
        filter: [],
        sort: [{
          fieldName: 'orderInDocument',
          direction: SortDirection.ASC,
        }],
      };

      this.loadingService.doLoading(
        this.documentDetailService.objectId$.pipe(
          take(1),
          switchMap(documentId => {
            if (this.canShowDocumentComponents) {
              return this.searchService.findEsslComponents(searchParams, documentId);
            }
            else if (this.objectExistenceComponentsVisible) {
              return this.searchService.findEsslComponentsWithObjectExistenceCheck(searchParams, documentId);
            }
            else {
              return of({content: [], totalElements: 0});
            }
          }),
        ),
        this,
        DocumentDetailPreviewLoaderId.DOCUMENT_COMPONENTS,
      ).subscribe(componentsPage => {
        this.documentComponents = componentsPage.content;
        this.componentsCount = componentsPage.totalElements;
        this.cd.detectChanges();
      });
    }
  }

  private initCustomFieldsValidityReferenceDate() {
    if (this.isDocumentSettled) {
      const settlementDate = this.form.get('settlementDate')!.value;
      this.customFieldsValidityReferenceDate = settlementDate ? new Date(settlementDate) : null;
    }
    else {
      this.customFieldsValidityReferenceDate = new Date();
    }
  }

  private initDocumentTypeChangeListener() {
    this.documentTypeIdSubscription?.unsubscribe();
    this.documentTypeIdSubscription = this.form.get('documentTypeId')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(documentTypeId => {
      if (documentTypeId) {
        const documentType = this.documentTypes.find(dt => dt.id === documentTypeId)!;
        const resolutionDateControl = this.form.get('resolutionDate')! as IczFormControl;
        const createDate = new Date(this.form.get('auditInfo')!.get('createdAt')!.value);
        if (documentType.statutoryPeriod && !this.isDocumentSettled) {
          if (documentType.statutoryPeriodUnit === StatutoryPeriodUnit.WORKING_DAYS) {
            let startDate: string;
            if (this.isReceivedDocument) {
              startDate = new Date(this.deliveryDate!).toISOString();
            } else {
              startDate = new Date(this.form.get('auditInfo')!.get('createdAt')!.value).toISOString();
            }
            this.loadingService.doLoading(
              this.apiCalendarHelperService.calendarHelperGetNthWorkingDay({n: documentType.statutoryPeriod, country: 'CZ', startDate}),
              this.parentDocumentDetailComponent!
            ).subscribe(date => {
              this.handleResolutionDateChange(new Date(date), resolutionDateControl);
            });
          } else {
            let resolutionDate: Date;
            if (this.isReceivedDocument) {
              const deliveryDate = new Date(this.deliveryDate!);
              resolutionDate = add(deliveryDate, {days: documentType.statutoryPeriod});
            } else {
              resolutionDate = add(createDate, {days: documentType.statutoryPeriod});
            }
            this.handleResolutionDateChange(resolutionDate, resolutionDateControl);
          }
        }
      }
    });
  }

  protected readonly SettlementType = SettlementType;
  protected readonly DisposalTipDetailLevel = DisposalTipDetailLevel;
  protected readonly isAuthorizedOperationGranted = isAuthorizedOperationGranted;
  protected readonly FileAuthorizedOperation = FileAuthorizedOperation;
  protected readonly EntityClassType = EntityClassType;
  protected readonly EntityClassSelectorUsage = EntityClassSelectorUsage;
}
